import React, { useState, useEffect } from 'react';
import { Modal, Tag, Spin, Empty, message } from 'antd';
import rbacService from '../../services/rbacService';

/**
 * View Permissions Modal Component
 * Displays effective permissions for a user
 */
export default function ViewPermissionsModal({ visible, user, onClose }) {
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && user) {
      fetchUserPermissions();
    }
  }, [visible, user]);

  const fetchUserPermissions = async () => {
    setLoading(true);
    try {
      const result = await rbacService.getUserPermissions(user.id);
      if (result.success) {
        const data = result.data;
        // API returns { roles: [...], permissions: string[] }
        const rawRoles = data?.roles;
        const rawPerms = data?.permissions;
        setRoles(Array.isArray(rawRoles) ? rawRoles : []);
        // Normalize: permissions can be array of strings or array of { id, key }
        const permList = Array.isArray(rawPerms)
          ? rawPerms.map((p) => (typeof p === 'string' ? { key: p } : p))
          : [];
        setPermissions(permList);
      } else {
        message.error('Failed to load permissions');
        setPermissions([]);
        setRoles([]);
      }
    } catch (error) {
      message.error('Error loading permissions');
      console.error('Error fetching user permissions:', error);
      setPermissions([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Effective Permissions - ${user?.name}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      <Spin spinning={loading}>
        {permissions.length === 0 && roles.length === 0 && !loading ? (
          <Empty description="No roles or permissions assigned" />
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            {roles.length > 0 && (
              <div className="mb-4">
                <div className="mb-2 text-sm font-medium text-gray-700">
                  Roles ({roles.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {roles.map((role) => (
                    <Tag key={role.id} color="green" className="px-3 py-1 text-sm">
                      {role.name}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
            <div>
              <div className="mb-2 text-sm font-medium text-gray-700">
                Permissions ({permissions.length})
              </div>
              {permissions.length === 0 ? (
                <p className="text-sm text-gray-500">No permissions assigned</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {permissions.map((permission) => (
                    <Tag
                      key={permission.id || permission.key}
                      color="blue"
                      className="mb-2 px-3 py-1 text-sm"
                    >
                      {permission.key}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  );
}
